import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const previousPathname = useRef<string>('');
  const isInitialMount = useRef(true);

  // List of pages that handle their own scroll restoration
  const selfManagedScrollPages = ['/leaderboards', '/leagues'];

  // Save scroll position when leaving a page
  useEffect(() => {
    // Don't save on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Save scroll position of the previous page
    if (previousPathname.current && previousPathname.current !== pathname) {
      const scrollPosition = window.scrollY;
      sessionStorage.setItem(`scroll_${previousPathname.current}`, scrollPosition.toString());
      //console.log(`Global: Saved scroll position for ${previousPathname.current}: ${scrollPosition}`);
    }

    // Update previous pathname for next navigation
    previousPathname.current = pathname;
  }, [pathname]);

  // Handle scroll restoration and new page scrolling
  useEffect(() => {
    // Skip scroll management for pages that handle their own scroll restoration
    if (selfManagedScrollPages.includes(pathname)) {
      //console.log(`Global: Skipping scroll management for ${pathname} (self-managed)`);
      return;
    }

    // If this is a back/forward navigation, restore the previous scroll position
    if (navigationType === 'POP') {
      const savedScrollPosition = sessionStorage.getItem(`scroll_${pathname}`);
      if (savedScrollPosition) {
        const scrollY = parseInt(savedScrollPosition);
        //console.log(`Global: Restoring scroll position for ${pathname}: ${scrollY}`);
        
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
        
        return;
      }
    }

    // For new navigation (PUSH/REPLACE), scroll to top
    if (navigationType === 'PUSH' || navigationType === 'REPLACE') {
      //console.log(`Global: New navigation to ${pathname}, scrolling to top`);
      window.scrollTo(0, 0);
    }
  }, [pathname, navigationType]);

  // Cleanup function to save scroll position when component unmounts
  useEffect(() => {
    return () => {
      if (pathname) {
        const scrollPosition = window.scrollY;
        sessionStorage.setItem(`scroll_${pathname}`, scrollPosition.toString());
        //console.log(`Global: Cleanup - Saved scroll position for ${pathname}: ${scrollPosition}`);
      }
    };
  }, [pathname]);

  return null;
};

export default ScrollToTop; 